import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AcademicCapIcon,
  ArrowRightIcon,
  BeakerIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  CommandLineIcon,
  CpuChipIcon,
  DocumentCheckIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  MapPinIcon,
  PlayCircleIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  TableCellsIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import claudeIcon from "@lobehub/icons-static-svg/icons/claude-color.svg";
import geminiIcon from "@lobehub/icons-static-svg/icons/gemini-color.svg";
import huggingFaceIcon from "@lobehub/icons-static-svg/icons/huggingface-color.svg";
import langchainIcon from "@lobehub/icons-static-svg/icons/langchain-color.svg";
import mistralIcon from "@lobehub/icons-static-svg/icons/mistral-color.svg";
import perplexityIcon from "@lobehub/icons-static-svg/icons/perplexity-color.svg";
import deepseekIcon from "@lobehub/icons-static-svg/icons/deepseek-color.svg";
import nvidiaIcon from "@lobehub/icons-static-svg/icons/nvidia-color.svg";
import { SparklesCore } from "./ui/sparkles";
import { TextGenerateEffect } from "./ui/text-generate-effect";
import { InfiniteMovingCards } from "./ui/infinite-moving-cards";
import {
  CardBody,
  CardContainer,
  CardItem,
} from "./ui/3d-card";

const navItems = [
  { label: "Position", href: "#position" },
  { label: "Tracks", href: "#tracks" },
  { label: "Builds", href: "#builds" },
  { label: "Admissions", href: "#admissions" },
  { label: "Outcomes", href: "#outcomes" },
];

const proofMetrics = [
  { value: "Offline", label: "Bengaluru-first cohorts", icon: MapPinIcon },
  { value: "Lab-heavy", label: "Mentor-led build reviews", icon: BeakerIcon },
  { value: "Small", label: "Screened cohort model", icon: UserGroupIcon },
  { value: "Artifact", label: "Portfolio over certificates", icon: DocumentCheckIcon },
];

const truthCards = [
  {
    title: "Implementation, not hype",
    body: "The academy focuses on workflows, context, harnesses, evaluation, deployment, and operational readiness.",
    icon: ServerStackIcon,
  },
  {
    title: "Reviewed artifacts",
    body: "Every serious project must run, explain itself, show eval evidence, and survive a capstone review.",
    icon: ClipboardDocumentCheckIcon,
  },
  {
    title: "Career honesty",
    body: "We tell the learners what each track can realistically prepare them for, and what it cannot.",
    icon: ShieldCheckIcon,
  },
];

const isNotPairs = [
  ["Production-first AI systems academy", "Prompt engineering academy"],
  ["Small-cohort offline learning studio", "Mass-market online course vendor"],
  ["Portfolio and artifact-driven academy", "Certificate-first training shop"],
  ["Realistic upskilling partner", "Placement-promise bootcamp"],
];

const tracks = [
  {
    key: "workflow",
    label: "Track 1",
    name: "AI Workflow & Business Systems Builder",
    pdf: "/track_1.pdf",
    short: "Business AI capability for serious beginners and professionals.",
    audience: "Freshers, analysts, PMs, founders, HR, finance, sales ops, service ops, and domain professionals.",
    outcome: "AI workflow and business automation capability.",
    fit: "For learners who need practical AI implementation ability without pretending to become engineers.",
    notFor: "Engineering role seekers, LLMOps aspirants, inference engineers, or deep ML learners.",
    duration: "8-week weekend or 10-week evening format",
    icon: BriefcaseIcon,
    accent: "sky",
    builds: [
      "AI-assisted reporting workflow",
      "Document AI / retrieval prototype",
      "Automation workflow with human-in-loop",
      "Implementation memo and demo video",
    ],
    modules: [
      "AI work model and structured prompting",
      "Spreadsheet workflows and structured outputs",
      "SQL and business data handling",
      "APIs, JSON, document AI, retrieval, webhooks",
    ],
    curriculum: [
      ["Week 1", "AI work model, structured prompting, responsible use, failure modes", "Structured AI task pack"],
      ["Week 2", "Spreadsheet workflows, report generation, structured outputs", "AI-assisted reporting workflow"],
      ["Week 3", "SQL and data handling for business users", "Query-based business analysis"],
      ["Week 4", "Lightweight Python, APIs, JSON", "Simple API-connected workflow"],
      ["Week 5", "Document AI, extraction, basic retrieval, context design", "Document assistant prototype"],
      ["Week 6", "Workflow automation, webhooks, human-in-loop", "Automation workflow"],
      ["Week 7", "Governance, ROI, implementation communication", "Implementation memo"],
      ["Week 8", "Capstone sprint and review", "Final demo"],
    ],
  },
  {
    key: "applied",
    label: "Track 2",
    name: "Applied AI Systems Builder",
    pdf: "/track_2.pdf",
    short: "Flagship technical program for applied AI implementation.",
    audience: "Software engineers, coding data analysts, automation engineers, SDETs, and technical consultants.",
    outcome: "Applied AI implementation and deployment readiness.",
    fit: "For technical learners who can already code and need to become AI implementation builders.",
    notFor: "True beginners, non-coders, pure researchers, or notebook-only learners.",
    duration: "10 weeks with weekly demos",
    icon: CodeBracketIcon,
    accent: "cyan",
    flagship: true,
    builds: [
      "Structured AI service",
      "RAG application with retrieval debugging",
      "Evaluation harness",
      "Reliable agent workflow and deployed capstone",
    ],
    modules: [
      "FastAPI, HTTP, auth, JSON, packaging",
      "LLM I/O, structured outputs, deterministic scaffolding",
      "Context engineering, RAG, evals, harness engineering",
      "Tool use, guardrails, deployment, tracing, cost and latency",
    ],
    curriculum: [
      ["Week 1", "Python packaging, FastAPI, HTTP, auth, JSON", "API-backed project skeleton"],
      ["Week 2", "LLM I/O, structured outputs, deterministic scaffolding", "Structured AI service"],
      ["Week 3", "Context engineering: prompts, memory, retrieval selection, compaction", "Context design lab"],
      ["Week 4", "RAG: chunking, embeddings, ranking, citations, retrieval debugging", "Retrieval application"],
      ["Week 5", "Evals: hallucination checks, task success, regression testing", "Evaluation harness"],
      ["Week 6", "Harness engineering: validators, retries, workflow control, state", "Reliable agent workflow"],
      ["Week 7", "Tool use, MCP concepts, workflow agents, when not to use agents", "Tool-using AI system"],
      ["Week 8", "Guardrails, access control, safety, logging, failure handling", "Hardened AI workflow"],
      ["Week 9", "Docker, cloud runtime, observability, cost and latency measurement", "Deployed service"],
      ["Week 10", "Capstone hardening, architecture review, demo day", "Final production-style artifact"],
    ],
  },
  {
    key: "advanced",
    label: "Track 3",
    name: "Advanced AI Systems Reliability & Inference",
    pdf: "/track_3.pdf",
    short: "Premium advanced track after proven systems depth.",
    audience: "Backend, platform, DevOps, SRE, ML, infra engineers, and strong Track 2 graduates.",
    outcome: "AI systems reliability, LLMOps, and inference-readiness.",
    fit: "For experienced engineers who already understand production systems and want AI reliability depth.",
    notFor: "Freshers, non-coders, or learners without production experience.",
    duration: "10-12 weeks after mature Track 2 cohorts",
    icon: CpuChipIcon,
    accent: "amber",
    builds: [
      "Reliability harness",
      "Evaluation suite and red-team set",
      "Cost / latency report",
      "Observability dashboard and incident runbook",
    ],
    modules: [
      "SLOs, latency budgets, failure budgets",
      "Advanced context, memory, CI/CD, serving patterns",
      "Batching, caching, quantization, runtime tradeoffs",
      "Security, enterprise integration, capstone defense",
    ],
    curriculum: [
      ["Week 1", "AI system architecture, SLOs, latency budgets, failure budgets", "System design brief"],
      ["Week 2", "Advanced context and memory design", "Context architecture"],
      ["Week 3", "Harness engineering: maintainability, behavior harnesses", "Reliability harness"],
      ["Week 4", "Evals at scale, red-teaming, regression, online/offline test sets", "Evaluation suite"],
      ["Week 5", "CI/CD, packaging, environment discipline", "Production pipeline"],
      ["Week 6", "Serving patterns: sync vs async, queues, routing, fallbacks", "Serving architecture"],
      ["Week 7", "Inference I: KV cache, batching, caching, quantization", "Benchmark lab"],
      ["Week 8", "Inference II: runtime tradeoffs and comparison", "Runtime comparison"],
      ["Week 9", "Edge/on-device inference, small models, hardware constraints", "Edge deployment prototype"],
      ["Week 10", "Observability, tracing, cost engineering, incident response", "Observability dashboard"],
      ["Week 11", "Security, policy, guardrails, enterprise integration", "Hardened system"],
      ["Week 12", "Capstone defense", "Final advanced artifact"],
    ],
  },
];

const studios = [
  {
    title: "Enterprise AI Implementation Studio",
    body: "For GCCs, startups, product teams, consulting teams, and operations teams moving GenAI from pilot to implementation.",
    icon: BuildingOffice2Icon,
    output: ["Use-case matrix", "Workflow map", "Evaluation rubric", "Working team prototype"],
  },
  {
    title: "Engineering College AI Studio",
    body: "For faculty teams and final-year students upgrading AI learning from notebooks to deployed capstones.",
    icon: AcademicCapIcon,
    output: ["Skill baseline", "Applied AI lab", "Deployment workflow", "Project rubric"],
  },
];

const buildArtifacts = [
  {
    name: "Runnable system",
    detail: "Code or workflow that runs outside a slide deck.",
    icon: CommandLineIcon,
  },
  {
    name: "Evaluation evidence",
    detail: "Checks, rubrics, regression tests, and failure notes.",
    icon: BeakerIcon,
  },
  {
    name: "Architecture notes",
    detail: "Design choices, tradeoffs, and operational boundaries.",
    icon: DocumentCheckIcon,
  },
  {
    name: "Demo narrative",
    detail: "A fast explanation of the problem, system, evidence, and value.",
    icon: PlayCircleIcon,
  },
];

const bridgeCourses = [
  {
    course: "Digital Workflows & Analytical Reasoning",
    route: "Before Track 1",
    icon: TableCellsIcon,
  },
  { course: "Python, SQL, Git, APIs", route: "Before Track 2", icon: CodeBracketIcon },
  { course: "Software Systems Basics", route: "Before Track 2", icon: Cog6ToothIcon },
  { course: "ML / NLP / LLM Foundations", route: "Before Track 2 or 3", icon: CpuChipIcon },
  { course: "Linux, Docker, Cloud, CI Basics", route: "Before Track 3", icon: ServerStackIcon },
];

const teach = [
  "Python, SQL, APIs, Git",
  "Context engineering",
  "RAG and retrieval quality",
  "Evals and hallucination debugging",
  "Harness engineering",
  "Guardrails and policy",
  "Deployment basics",
  "Observability and logging",
  "Cost optimization",
  "Portfolio and mock interviews",
];

const deEmphasize = [
  "Standalone prompt-engineering flagship",
  "Certificate-first design",
  "One-size-fits-all syllabus",
  "Pure tool-demo sessions",
  "Guaranteed placement claims",
  "Beginner-to-advanced role shortcuts",
  "Capstones without demos",
  "Projects without evaluation",
];

const admissions = [
  {
    label: "Track 1",
    detail: "Reasoning, spreadsheet comfort, communication clarity, and commitment.",
    icon: TableCellsIcon,
  },
  {
    label: "Track 2",
    detail: "Python/API mini-project, debugging, and problem decomposition.",
    icon: CodeBracketIcon,
  },
  {
    label: "Track 3",
    detail: "Coding, systems design, Docker/Linux basics, and prior deployment review.",
    icon: ServerStackIcon,
  },
  {
    label: "Enterprise / College",
    detail: "Sponsor goals, team readiness, use-case maturity, and lab infrastructure.",
    icon: BuildingOffice2Icon,
  },
];

const outcomes = [
  {
    quote:
      "Track 1 prepares learners for AI-adjacent analyst, workflow, automation, and internal upskilling roles.",
    name: "Track 1",
    title: "Business AI capability",
  },
  {
    quote:
      "Track 2 prepares already-technical learners for applied AI implementation roles through runnable systems and eval evidence.",
    name: "Track 2",
    title: "Applied AI systems",
  },
  {
    quote:
      "Track 3 is for experienced engineers moving toward reliability, LLMOps, and inference-adjacent systems work.",
    name: "Track 3",
    title: "Advanced reliability",
  },
  {
    quote:
      "Enterprise and college studios focus on team capability, faculty readiness, better capstones, and measurable implementation artifacts.",
    name: "Institutional Studio",
    title: "Team implementation",
  },
];

const faqs = [
  [
    "Does Autoficial Labs Academy guarantee jobs?",
    "No. Autoficial Labs Academy provides reviewed artifacts, portfolio readiness, mock interviews, demo days, and network access. It does not guarantee jobs.",
  ],
  [
    "Is this a prompt engineering course?",
    "No. Prompting appears only as one part of AI systems work. The academy focuses on workflows, context, harnesses, evals, deployment, and implementation.",
  ],
  [
    "Can a beginner join?",
    "Beginners can join Track 1. Technical tracks require screening or bridge completion.",
  ],
  [
    "Does Track 2 require coding?",
    "Yes. Track 2 requires Python basics, APIs, JSON literacy, and debugging ability.",
  ],
  [
    "Are classes online or offline?",
    "The academy runs offline-first cohorts in Bengaluru with support materials and review resources online.",
  ],
];

const ecosystemIcons = [
  ["Claude", claudeIcon],
  ["Gemini", geminiIcon],
  ["Hugging Face", huggingFaceIcon],
  ["LangChain", langchainIcon],
  ["Mistral", mistralIcon],
  ["Perplexity", perplexityIcon],
  ["DeepSeek", deepseekIcon],
  ["NVIDIA", nvidiaIcon],
];

function Home() {
  const [activeTrack, setActiveTrack] = useState("applied");
  const selectedTrack = useMemo(
    () => tracks.find((track) => track.key === activeTrack) || tracks[1],
    [activeTrack]
  );

  return (
    <main className="academy-site min-h-screen bg-[#050608] text-slate-100">
      <Navigation />
      <Hero />
      <SignalStrip />
      <Position />
      <Tracks
        activeTrack={activeTrack}
        setActiveTrack={setActiveTrack}
        selectedTrack={selectedTrack}
      />
      <BuildStandard />
      <Studios />
      <BridgeAndBoundaries />
      <Admissions />
      <Outcomes />
      <Faq />
      <Contact />
      <Footer />
    </main>
  );
}

function Navigation() {
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#050608]/82 backdrop-blur-2xl">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href="#top" className="flex min-w-0 items-center gap-3" aria-label="Autoficial Labs Academy home">
          <img
            src="/logo-dark.png"
            alt="Autoficial Labs Academy"
            className="h-10 w-auto max-w-[210px] object-contain sm:max-w-[280px]"
          />
        </a>
        <div className="hidden items-center gap-1 rounded-full border border-white/10 bg-white/[0.035] p-1 lg:flex">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="rounded-full px-3 py-2 text-sm text-slate-400 transition hover:bg-white/8 hover:text-white"
            >
              {item.label}
            </a>
          ))}
        </div>
        <a
          href="#contact"
          className="group inline-flex items-center gap-2 rounded-full border border-sky-300/35 bg-sky-300/10 px-4 py-2 text-sm font-semibold text-sky-100 transition hover:border-sky-200 hover:bg-sky-300 hover:text-slate-950"
        >
          Apply
          <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </a>
      </nav>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative isolate overflow-hidden px-4 pt-28 sm:px-6 lg:px-8">
      <div className="academy-grid absolute inset-0 opacity-75" />
      <div className="academy-beams absolute inset-x-0 top-0 h-[620px]" />
      <SparklesCore
        id="academy-hero-particles"
        background="transparent"
        minSize={0.3}
        maxSize={0.9}
        particleDensity={24}
        particleColor="#9ca3af"
        className="absolute inset-0 h-full w-full opacity-25"
      />
      <div className="relative z-10 mx-auto grid min-h-[calc(100vh-7rem)] max-w-7xl items-center gap-12 pb-20 lg:grid-cols-[1.02fr_0.98fr]">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-4xl"
        >
          <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.045] px-3 py-2 text-sm text-slate-300 shadow-2xl shadow-black/30">
            <span className="h-2 w-2 rounded-full bg-sky-300" />
            Bengaluru offline-first AI systems academy
          </div>
          <h1 className="text-balance text-5xl font-semibold leading-[0.98] tracking-normal text-white sm:text-6xl lg:text-7xl">
            Build AI systems that survive production.
          </h1>
          <p className="mt-7 max-w-3xl text-pretty text-lg leading-8 text-slate-300">
            Autoficial Labs Academy trains serious learners, professionals, engineers,
            and teams to build, evaluate, deploy, and operationalize AI through
            mentor-led labs, reviewed artifacts, and capstone discipline.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a className="academy-primary-button" href="#contact">
              Apply for the Next Cohort
              <ArrowRightIcon className="h-4 w-4" />
            </a>
            <a className="academy-secondary-button" href="#contact">
              <PlayCircleIcon className="h-5 w-5" />
              Attend a Build Clinic
            </a>
          </div>
          <div className="mt-10 grid max-w-3xl grid-cols-2 gap-3 sm:grid-cols-4">
            {proofMetrics.map((metric) => {
              const Icon = metric.icon;
              return (
              <div key={metric.value} className="rounded-lg border border-white/10 bg-white/[0.035] p-4">
                <Icon className="mb-4 h-5 w-5 text-sky-200" />
                <p className="text-lg font-semibold text-white">{metric.value}</p>
                <p className="mt-1 text-xs leading-5 text-slate-400">{metric.label}</p>
              </div>
              );
            })}
          </div>
        </motion.div>
        <SystemMap />
      </div>
    </section>
  );
}

function SystemMap() {
  return (
    <>
      <MobileSystemMap />
      <CardContainer containerClassName="hidden py-0 sm:flex">
      <CardBody className="relative h-[580px] w-full max-w-[560px] rounded-[1.5rem] border border-white/10 bg-[#080b0f]/90 p-4 shadow-2xl shadow-black/50">
        <CardItem translateZ={45} className="w-full">
          <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.035] px-4 py-3">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Implementation Lab</p>
              <p className="mt-1 text-sm font-medium text-white">Production readiness board</p>
            </div>
            <span className="rounded-full border border-sky-300/30 bg-sky-300/10 px-3 py-1 text-xs text-sky-100">
              Live Review
            </span>
          </div>
        </CardItem>
        <CardItem translateZ={80} className="mt-4 grid w-full grid-cols-[1fr_0.78fr] gap-3">
          <div className="rounded-xl border border-white/10 bg-black/30 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">System spine</p>
            <div className="mt-5 space-y-3">
              {["Context", "Harness", "Evals", "Deployment"].map((item, index) => (
                <div key={item} className="flex items-center gap-3">
                  <span className="grid h-7 w-7 place-items-center rounded-md bg-white/[0.06] text-xs text-slate-300">
                    {index + 1}
                  </span>
                  <span className="text-sm text-slate-200">{item}</span>
                  <span className="ml-auto h-px flex-1 bg-gradient-to-r from-slate-700 to-transparent" />
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-cyan-300/20 bg-cyan-300/[0.06] p-4">
            <CpuChipIcon className="h-7 w-7 text-cyan-200" />
            <p className="mt-8 text-3xl font-semibold text-white">AI</p>
            <p className="mt-2 text-sm leading-6 text-cyan-50/75">
              The model is one part of the system. The academy trains the layer around it.
            </p>
          </div>
        </CardItem>
        <CardItem translateZ={105} className="mt-3 grid w-full grid-cols-3 gap-3">
          {[
            ["Retrieval quality", BeakerIcon],
            ["Failure handling", ShieldCheckIcon],
            ["Operational notes", DocumentCheckIcon],
          ].map(([label, Icon]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-white/[0.035] p-3">
              <Icon className="h-5 w-5 text-sky-200" />
              <p className="mt-3 text-xs leading-5 text-slate-300">{label}</p>
            </div>
          ))}
        </CardItem>
        <CardItem translateZ={70} className="mt-3 w-full">
          <div className="rounded-xl border border-white/10 bg-black/35 p-4">
            <div className="grid grid-cols-4 gap-2">
              {ecosystemIcons.slice(0, 8).map(([name, src]) => (
                <span
                  key={name}
                  className="grid h-12 place-items-center rounded-lg border border-white/10 bg-white/[0.035]"
                  title={name}
                >
                  <img src={src} alt="" className="h-5 w-5 opacity-95" />
                </span>
              ))}
            </div>
            <p className="mt-4 text-xs leading-5 text-slate-500">
              Tools change. The implementation discipline stays.
            </p>
          </div>
        </CardItem>
      </CardBody>
    </CardContainer>
    </>
  );
}

function MobileSystemMap() {
  return (
    <div className="sm:hidden rounded-2xl border border-white/10 bg-[#080b0f]/92 p-4 shadow-2xl shadow-black/40">
      <div className="flex items-start justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.035] p-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Implementation Lab</p>
          <p className="mt-1 text-sm font-semibold text-white">Production readiness board</p>
        </div>
        <span className="shrink-0 rounded-full border border-sky-300/30 bg-sky-300/10 px-2.5 py-1 text-[11px] text-sky-100">
          Review
        </span>
      </div>
      <div className="mt-4 grid gap-3">
        {["Context", "Harness", "Evals", "Deployment"].map((item, index) => (
          <div key={item} className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/25 p-3">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-sky-300/10 text-xs font-semibold text-sky-100">
              {index + 1}
            </span>
            <span className="text-sm font-medium text-slate-200">{item}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {[
          ["Retrieval", BeakerIcon],
          ["Failure", ShieldCheckIcon],
          ["Notes", DocumentCheckIcon],
        ].map(([label, Icon]) => (
          <div key={label} className="rounded-xl border border-white/10 bg-white/[0.035] p-3">
            <Icon className="h-5 w-5 text-sky-200" />
            <p className="mt-2 text-xs leading-4 text-slate-300">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignalStrip() {
  return (
    <section className="border-y border-white/10 bg-white/[0.025] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <p className="text-sm font-medium text-slate-300">
          Most AI courses teach tools.{" "}
          <span className="academy-highlight-implementation">
            Autoficial Labs teaches implementation.
          </span>
        </p>
        <div className="flex flex-wrap gap-2">
          {["Context", "Harnesses", "Evals", "RAG", "Guardrails", "Deployment"].map((item) => (
            <span key={item} className="rounded-full border border-white/10 bg-[#050608] px-3 py-1 text-xs text-slate-400">
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Position() {
  return (
    <section id="position" className="academy-section">
      <SectionHeading
        eyebrow="Position"
        title="A serious academy for implementation-ready learners and teams."
        bodyNode={
          <>
            Our promise is deliberately narrow:{" "}
            <span className="academy-highlight-implementation">
              build AI systems that work beyond demos
            </span>
            .
          </>
        }
      />
      <div className="mt-10 grid gap-4 lg:grid-cols-3">
        {truthCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <SpotlightCard key={card.title} index={index}>
              <Icon className="h-7 w-7 text-sky-200" />
              <h3 className="mt-6 text-xl font-semibold text-white">{card.title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-400">{card.body}</p>
            </SpotlightCard>
          );
        })}
      </div>
      <CompareBlock />
    </section>
  );
}

function CompareBlock() {
  return (
    <div className="academy-compare mt-10">
      <div className="academy-compare-pane academy-positive-pane">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-200">
          Our Academy is
        </p>
        <div className="mt-5 space-y-3">
          {isNotPairs.map(([is]) => (
            <div key={is} className="academy-compare-item">
              <CheckCircleIcon className="h-5 w-5 shrink-0 text-sky-200" />
              {is}
            </div>
          ))}
        </div>
      </div>
      <div className="academy-compare-pane academy-negative-pane">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-200">
          We are not
        </p>
        <div className="mt-5 space-y-3">
          {isNotPairs.map(([, not]) => (
            <div key={not} className="academy-compare-item">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-rose-200" />
              {not}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Tracks({ activeTrack, setActiveTrack, selectedTrack }) {
  const Icon = selectedTrack.icon;
  return (
    <section id="tracks" className="academy-section">
      <SectionHeading
        eyebrow="Program Portfolio"
        title="Three public tracks with one clear flagship."
        bodyNode={
          <>
            <span className="academy-highlight-implementation">
              Track 2 is the flagship technical program.
            </span>{" "}
            Track 3 stays advanced by design and launches after proven cohort quality.
          </>
        }
      />
      <div className="mt-10 space-y-6">
        <div className="grid gap-3 lg:grid-cols-3">
          {tracks.map((track) => {
            const TrackIcon = track.icon;
            const active = activeTrack === track.key;
            return (
              <button
                key={track.key}
                onClick={() => setActiveTrack(track.key)}
                className={`academy-track-tab ${track.key === "applied" ? "academy-track-tab-featured" : ""} ${active ? "is-active" : ""}`}
              >
                <TrackIcon className="h-6 w-6 shrink-0" />
                <span className="text-left">
                  <span className="block text-xs uppercase tracking-[0.2em] text-slate-500">{track.label}</span>
                  <span className="mt-1 block font-semibold">{track.name}</span>
                </span>
              </button>
            );
          })}
        </div>
        <motion.div
          key={selectedTrack.key}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28 }}
          className="overflow-hidden rounded-2xl border border-white/10 bg-[#080b0f]"
        >
          <div className="grid gap-0 2xl:grid-cols-[1fr_0.92fr]">
            <div className="p-6 sm:p-8 lg:p-10">
              <div className="flex items-center justify-between gap-4">
                <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs uppercase tracking-[0.2em] text-slate-400">
                  {selectedTrack.duration}
                </span>
                {selectedTrack.flagship && (
                  <span className="rounded-full border border-cyan-300/30 bg-cyan-300/10 px-3 py-1 text-xs font-semibold text-cyan-100">
                    Flagship
                  </span>
                )}
              </div>
              <Icon className="mt-8 h-9 w-9 text-sky-200" />
              <h3 className="mt-5 text-3xl font-semibold text-white">{selectedTrack.name}</h3>
              <p className="mt-4 text-base leading-7 text-slate-300">{selectedTrack.fit}</p>
              <div className="mt-7 grid gap-4 sm:grid-cols-2">
                <InfoTile icon={UserGroupIcon} label="Audience" value={selectedTrack.audience} />
                <InfoTile icon={FlagIcon} label="Outcome" value={selectedTrack.outcome} />
              </div>
            </div>
            <div className="border-t border-white/10 bg-white/[0.025] p-6 sm:p-8 lg:p-10 2xl:border-l 2xl:border-t-0">
              <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
                <WrenchScrewdriverIcon className="h-5 w-5 text-sky-200" />
                Portfolio signals
              </p>
              <div className="mt-5 space-y-3">
                {selectedTrack.builds.slice(0, 3).map((item) => (
                  <div key={item} className="academy-build-line">
                    <ChevronRightIcon className="h-4 w-4 text-sky-200" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="mt-8 rounded-xl border border-sky-300/20 bg-sky-300/10 p-4">
                <p className="text-sm font-semibold text-sky-50">View Full Curriculum</p>
                <p className="mt-2 text-sm leading-6 text-sky-50/70">
                  Use the curriculum download for weekly modules, outputs, artifact expectations, and detailed track positioning.
                </p>
                <a
                  href={selectedTrack.pdf}
                  download
                  className="academy-primary-button mt-5 w-full sm:w-auto"
                >
                  Download Details
                  <ArrowRightIcon className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function BuildStandard() {
  return (
    <section id="builds" className="academy-section">
      <SectionHeading
        eyebrow="Build Standard"
        title="Every serious project must prove more than a demo."
        bodyNode={
          <>
            Our promise is artifact quality:{" "}
            <span className="academy-highlight-implementation">
              runnable systems, evidence, architecture, and communication.
            </span>
          </>
        }
      />
      <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {buildArtifacts.map((item, index) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: index * 0.06 }}
              className="academy-artifact-card"
            >
              <Icon className="h-7 w-7 text-cyan-200" />
              <h3 className="mt-8 text-xl font-semibold text-white">{item.name}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-400">{item.detail}</p>
            </motion.div>
          );
        })}
      </div>
      <div className="mt-10 overflow-hidden rounded-2xl border border-white/10 bg-[#080b0f] p-4">
        <InfiniteMovingCards items={outcomes} direction="right" speed="slow" />
      </div>
    </section>
  );
}

function Studios() {
  return (
    <section className="academy-section">
      <SectionHeading
        eyebrow="Institutional Track"
        title="Enterprise and college AI implementation studios."
        bodyNode={
          <>
            Custom programs help teams and institutions{" "}
            <span className="academy-highlight-implementation">
              move from AI awareness to usable implementation capability
            </span>
            .
          </>
        }
      />
      <div className="mt-10 grid gap-5 lg:grid-cols-2">
        {studios.map((studio) => {
          const Icon = studio.icon;
          return (
            <div key={studio.title} className="academy-studio-card">
              <Icon className="h-8 w-8 text-sky-200" />
              <h3 className="mt-8 text-2xl font-semibold text-white">{studio.title}</h3>
              <p className="mt-4 text-sm leading-6 text-slate-400">{studio.body}</p>
              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                {studio.output.map((item) => (
                  <div key={item} className="rounded-lg border border-white/10 bg-[#050608] p-3 text-sm text-slate-300">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function BridgeAndBoundaries() {
  return (
    <section className="academy-section">
      <div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr]">
        <div>
          <SectionHeading
            eyebrow="Readiness"
            title="Our bridge courses protect cohort quality."
            body="Learners are routed into the right level before they enter technical tracks."
          />
          <div className="academy-timeline mt-8">
            {bridgeCourses.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.course} className="academy-timeline-row">
                  <span className="academy-timeline-dot">
                    <Icon className="h-5 w-5 text-amber-100" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-white">{item.course}</span>
                    <span className="mt-1 block text-xs uppercase tracking-[0.16em] text-amber-200/75">{item.route}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <BoundaryPanel title="We teach" items={teach} positive />
          <BoundaryPanel title="We do not emphasize" items={deEmphasize} />
        </div>
      </div>
    </section>
  );
}

function Admissions() {
  return (
    <section id="admissions" className="academy-section">
      <SectionHeading
        eyebrow="Admissions Policy"
        titleNode={
          <>
            <span className="academy-highlight-implementation">Screening</span> exists to
            protect learner outcomes.
          </>
        }
        body="Our admission process filters for fit, readiness, and commitment instead of filling mass cohorts."
      />
      <div className="mt-10 grid gap-4 md:grid-cols-2">
        {admissions.map((item, index) => {
          const Icon = item.icon;
          return (
          <div key={item.label} className="academy-admission-card">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md border border-cyan-300/20 bg-cyan-300/10 text-sm text-cyan-100">
              <Icon className="h-6 w-6" />
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Screen {index + 1}</p>
              <h3 className="text-lg font-semibold text-white">{item.label}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-400">{item.detail}</p>
            </div>
          </div>
          );
        })}
      </div>
    </section>
  );
}

function Outcomes() {
  return (
    <section id="outcomes" className="academy-section">
      <div className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#080b0f]">
        <div className="grid gap-0 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="p-6 sm:p-8 lg:p-10">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-200">
              Outcome Truths
            </p>
            <h2 className="mt-5 text-4xl font-semibold leading-tight text-white">
              Honest pathways. No inflated promises.
            </h2>
            <p className="mt-5 text-sm leading-7 text-slate-400">
              The outcome model is:{" "}
              <span className="academy-highlight-implementation">
                portfolio review, mock interviews, demo days, reviewer network
                access, capstone defense, and learner effort.
              </span>              
            </p>
          </div>
          <div className="border-t border-white/10 p-6 sm:p-8 lg:border-l lg:border-t-0 lg:p-10">
            <div className="space-y-3">
              {[
                "No 100% placement promise",
                "No senior AI role promise for beginners",
                "No inference engineering promise without systems depth",
                "No research role promise from applied programs",
                "No salary inflation claims",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-lg border border-white/10 bg-[#050608] p-3 text-sm text-slate-300">
                  <ShieldCheckIcon className="h-5 w-5 text-sky-200" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section id="faq" className="academy-section">
      <div className="grid gap-10 lg:grid-cols-[0.45fr_0.55fr]">
        <SectionHeading
          eyebrow="FAQ"
          title="Straight answers before someone applies."
          body="The academy is designed for serious learners and teams who care about real systems work."
        />
        <div className="grid gap-3">
          {faqs.map(([question, answer], index) => (
            <details key={question} className="academy-faq-card group">
              <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-white">
                <span className="flex gap-3">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-cyan-300/10 text-xs text-cyan-100">
                    {index + 1}
                  </span>
                  {question}
                </span>
                <span className="text-slate-500 transition group-open:rotate-45 group-open:text-sky-200">+</span>
              </summary>
              <p className="mt-4 pl-9 text-sm leading-6 text-slate-400">{answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    program: "",
    background: "",
    problem: "",
  });

  const updateField = (field) => (event) => {
    setFormData((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const subject = "Autoficial Labs Academy - Interest Brief";
    const body = [
      `Full name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Program interest: ${formData.program}`,
      `Current background: ${formData.background}`,
      "",
      "Problem to solve:",
      formData.problem,
    ].join("\n");

    window.location.href = `mailto:academy@autoficial.ltd?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <section id="contact" className="px-4 py-24 sm:px-6 lg:px-8">
      <div className="academy-contact mx-auto max-w-7xl overflow-hidden rounded-[1.5rem] border border-sky-300/20">
        <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[0.9fr_1.1fr] lg:p-10">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-100">
              Next Cohort Interest
            </p>
            <h2 className="mt-5 max-w-3xl text-4xl font-semibold leading-tight text-white sm:text-5xl">
              Apply for fit. Attend a build clinic. See the standard.
            </h2>
            <p className="mt-5 max-w-2xl text-sm leading-7 text-sky-50/70">
              Bring a real workflow or system problem. The academy will route you
              toward the right track, bridge, or institutional format.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-sky-300/20 bg-sky-300/10 p-4">
                <p className="text-sm font-semibold text-sky-50">Apply for the Next Cohort</p>
                <p className="mt-2 text-xs leading-5 text-sky-50/65">Best for learners ready to be screened for Track 1, Track 2, or Track 3.</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <p className="text-sm font-semibold text-white">Attend a Build Clinic</p>
                <p className="mt-2 text-xs leading-5 text-slate-400">Best for evaluating the academy standard before choosing a track.</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#050608]/78 p-5">
            <div className="flex items-center gap-3">
              <EnvelopeIcon className="h-6 w-6 text-sky-200" />
              <p className="text-sm font-semibold text-white">Interest brief</p>
            </div>
            <form onSubmit={handleSubmit} className="mt-5 grid gap-3 sm:grid-cols-2">
              <input className="academy-input" aria-label="Full name" placeholder="Full name" value={formData.name} onChange={updateField("name")} />
              <input className="academy-input" aria-label="Email address" placeholder="Email address" value={formData.email} onChange={updateField("email")} />
              <select className="academy-input" aria-label="Program interest" value={formData.program} onChange={updateField("program")}>
                <option value="" disabled>Program interest</option>
                <option>Track 1</option>
                <option>Track 2</option>
                <option>Track 3</option>
                <option>Enterprise / College Studio</option>
                <option>Build Clinic</option>
              </select>
              <input className="academy-input" aria-label="Current background" placeholder="Current background" value={formData.background} onChange={updateField("background")} />
              <textarea className="academy-input min-h-28 sm:col-span-2" aria-label="Problem to solve" placeholder="One real workflow or system problem you want to solve with AI" value={formData.problem} onChange={updateField("problem")} />
              <button type="submit" className="academy-hover-border sm:col-span-2">
                <span>Submit interest brief</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10 px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-sm font-semibold tracking-[0.18em] text-white">AUTOFICIAL LABS ACADEMY</p>
          <p className="mt-2 text-sm text-slate-500">AI implementation education for real-world systems.</p>
        </div>
        <div className="grid gap-2 text-sm text-slate-500 lg:text-right">
          <a
            href="https://linkedin.com/company/autoficial-labs"
            target="_blank"
            rel="noreferrer"
            className="text-sky-200 transition hover:text-sky-100"
          >
            linkedin.com/company/autoficial-labs
          </a>
          <p>
            8th CROSS, CL RAMAIAH LAYOUT<br />
            R T Nagar, BANGALORE NORTH
          </p>
        </div>
      </div>
    </footer>
  );
}

function SectionHeading({ eyebrow, title, titleNode, body, bodyNode }) {
  return (
    <div className="max-w-3xl">
      <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-200">{eyebrow}</p>
      <h2 className="mt-4 text-balance text-3xl font-semibold leading-tight text-white sm:text-5xl">
        {titleNode || title}
      </h2>
      {bodyNode ? (
        <p className="mt-5 text-pretty text-sm leading-7 text-slate-400 sm:text-base">
          {bodyNode}
        </p>
      ) : (
        <TextGenerateEffect
          words={body}
          className="mt-3"
          textClassName="text-slate-400"
        />
      )}
    </div>
  );
}

function SpotlightCard({ children, index, alert = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ delay: index * 0.08 }}
      className={`academy-spotlight-card ${alert ? "academy-alert-card" : ""}`}
    >
      {children}
    </motion.div>
  );
}

function InfoTile({ label, value, icon: Icon = DocumentCheckIcon }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.035] p-4">
      <Icon className="mb-3 h-5 w-5 text-sky-200" />
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className="mt-2 text-sm leading-6 text-slate-300">{value}</p>
    </div>
  );
}

function BoundaryPanel({ title, items, positive = false }) {
  const Icon = positive ? CheckCircleIcon : XMarkIcon;
  return (
    <div className={`rounded-2xl border p-5 ${positive ? "border-white/10 bg-white/[0.025]" : "border-rose-400/25 bg-rose-500/[0.055]"}`}>
      <h3 className="text-2xl font-semibold text-white">{title}</h3>
      <div className="mt-6 space-y-2">
        {items.map((item) => (
          <div key={item} className={`flex gap-3 rounded-lg border p-3 text-sm leading-6 ${positive ? "border-white/10 bg-[#050608] text-slate-300" : "border-rose-400/20 bg-[#13070a] text-rose-50/80"}`}>
            <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${positive ? "text-sky-200" : "text-rose-200"}`} />
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Home;
